import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, streamJob } from "../api/client";
import { toast } from "../components/toast";

export type ReproofState = "idle" | "pending" | "running";

/**
 * Correcting an audio boundary saves the page instantly; this hook batches a
 * chapter's start+end edits into ONE background re-proof (debounced ~2.5s) so
 * the reviewer never sits through a per-boundary job. The job shows in the
 * Activity tray; the report/project queries refresh themselves when it lands.
 * A 409 (project busy with another job) retries until the project frees up.
 *
 * Status is tracked PER CHAPTER so the take list can show which files are
 * re-proofing (several may be queued — re-proofs serialise server-side, so
 * typically one "running" plus zero-or-more "pending").
 *
 * - scheduleReproof(chapterId): call after each boundary save — (re)arms the
 *   debounce so start+end fold into a single pass.
 * - statusOf(chapterId): "pending" | "running" | "idle" for a per-row indicator.
 * - reproofState: derived "any active" state, for a simple banner.
 * - flushReproof(): fire still-pending re-proofs now (reviewer is leaving).
 */
export function useBoundaryReproof(projectId: string | undefined) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<Record<string, ReproofState>>({});
  const timers = useRef<Record<string, number>>({});       // debounce/retry timers
  const watchers = useRef<Record<string, () => void>>({});  // streamJob closers

  function mark(chapterId: string, s: ReproofState) {
    setStatus((m) => {
      if (s === "idle") {
        if (!(chapterId in m)) return m;
        const next = { ...m };
        delete next[chapterId];
        return next;
      }
      if (m[chapterId] === s) return m;
      return { ...m, [chapterId]: s };
    });
  }

  function done(chapterId: string, jobStatus: string) {
    mark(chapterId, "idle");
    watchers.current[chapterId]?.();
    delete watchers.current[chapterId];
    qc.invalidateQueries({ queryKey: ["result", projectId] });
    qc.invalidateQueries({ queryKey: ["project", projectId] });
    toast(jobStatus === "completed" ? "Audio file re-proofed with the new boundaries." : "Re-proof stopped — check the log.",
      jobStatus === "completed" ? "success" : "error");
  }

  function fire(chapterId: string) {
    delete timers.current[chapterId];
    api<{ job_id: string | null }>(
      `/api/modules/proofing/projects/${projectId}/chapters/${chapterId}/reproof`,
      { method: "POST" },
    ).then((r) => {
      if (!r.job_id) { mark(chapterId, "idle"); return; }
      mark(chapterId, "running");
      watchers.current[chapterId]?.();
      watchers.current[chapterId] = streamJob(r.job_id, { onEnd: (m) => done(chapterId, m?.status ?? "completed") });
    }).catch((e: any) => {
      if (e?.status === 409) {
        mark(chapterId, "pending"); // project busy — retry once it frees up
        timers.current[chapterId] = window.setTimeout(() => fire(chapterId), 4000);
      } else {
        mark(chapterId, "idle");
        toast(e?.message || "Couldn't start the re-proof.", "error");
      }
    });
  }

  function scheduleReproof(chapterId: string) {
    mark(chapterId, "pending");
    if (timers.current[chapterId]) window.clearTimeout(timers.current[chapterId]);
    timers.current[chapterId] = window.setTimeout(() => fire(chapterId), 2500);
  }

  function flushReproof() {
    for (const cid of Object.keys(timers.current)) {
      window.clearTimeout(timers.current[cid]);
      fire(cid); // fire() deletes its own timer entry
    }
  }

  // Unmount: fire any still-debouncing re-proofs (fire-and-forget — the component
  // is gone, so no watcher) so a saved boundary isn't left un-re-proofed; tear
  // down live watchers. (flushReproof clears timers, so no double-fire here.)
  useEffect(() => () => {
    for (const cid of Object.keys(timers.current)) {
      window.clearTimeout(timers.current[cid]);
      api(`/api/modules/proofing/projects/${projectId}/chapters/${cid}/reproof`, { method: "POST" }).catch(() => {});
    }
    for (const close of Object.values(watchers.current)) close();
  }, []);

  const values = Object.values(status);
  const reproofState: ReproofState = values.includes("running") ? "running" : values.includes("pending") ? "pending" : "idle";
  const statusOf = (chapterId: string): ReproofState => status[chapterId] ?? "idle";

  return { reproofState, statusOf, scheduleReproof, flushReproof };
}
